import { useQuery } from '@apollo/client';
import moment from 'moment';
import { logger } from '../../Util';
import { isFeatureEnabled } from '../../config/FeaturesConfig';
import useInspireOptions from '../../hooks/useInspireOptions';
import { getLocaleStringFormatted } from '../../translations';
import { useMemo } from 'react';
import { getResourceHubDataQuery } from './queries';

export interface ParsedResourceHubAnnouncement {
  content: string;
  metadata: {
    videoUrl?: string;
    featureFlag?: string;
    launchDate?: moment.Moment;
  };
  guid: string;
}

const useResourceHubAnnouncements = () => {
  const { isOm } = useInspireOptions();

  const { loading, error, data } = useQuery(getResourceHubDataQuery, {
    variables: {
      locale: getLocaleStringFormatted() ?? 'en-US',
      isOm: isOm,
    },
    fetchPolicy: 'cache-first',
  });

  const resourceHubData = data?.getResourceHubData;

  const processedData = useMemo(
    () =>
      resourceHubData
        ?.map((announcement): ParsedResourceHubAnnouncement | null => {
          try {
            const metadata = JSON.parse(announcement.metadata);
            if (metadata.launchDate) {
              metadata.launchDate = moment(metadata.launchDate);
            }

            return { ...announcement, metadata };
          } catch (e) {
            logger.error('Error parsing ResourceHub announcement', e as Error);
            return null;
          }
        })
        .filter(
          (announcement): announcement is ParsedResourceHubAnnouncement => {
            if (!announcement) return false;

            if (!announcement.metadata.featureFlag) return true;

            return isFeatureEnabled(announcement.metadata.featureFlag);
          }
        )
        .sort((a, b) => {
          if (!a.metadata.launchDate || !b.metadata.launchDate) return 0;
          return b.metadata.launchDate.diff(a.metadata.launchDate);
        }),
    [resourceHubData]
  );

  return {
    loading,
    error,
    data: processedData,
  };
};

export default useResourceHubAnnouncements;
